// Tool-name humanization (fn-48) — raw tool names NEVER render in
// end-user UI. Curated overrides cover the tools whose mechanical
// expansion reads wrong (`ask_user_question` → "ask user question");
// everything else goes through the generic snake/dot-case → lowercase
// words fallback. Technical detail (the raw name, args JSON) lives
// behind a "Show details" disclosure — operator-grade, opt-in.
/**
 * Curated gerund phrases per tool name. Values are mid-sentence
 * lowercase gerunds ("asking you a question") so callers can compose
 * them into activity lines ("Asking you a question…") or approval
 * framing ("<Agent> wants to …" via `toolGerund`-adjacent forms in
 * later tasks).
 */
const TOOL_GERUND_OVERRIDES = {
    ask_user_question: 'asking you a question',
    bash: 'running a command',
    code_execution: 'running code',
    str_replace_based_edit_tool: 'editing a file',
    text_editor: 'editing a file',
    web_search: 'searching the web',
    web_fetch: 'reading a web page',
};
/**
 * Generic mechanical humanization: snake / dot / dash / camel case →
 * lowercase words. `"matter.search"` → `"matter search"`,
 * `"update_matter"` → `"update matter"`. Never returns the raw id —
 * an empty/degenerate name humanizes to `"a tool"`.
 */
export function humanizeToolName(name) {
    const words = name
        .replace(/([a-z0-9])([A-Z])/g, '$1 $2')
        .split(/[\s_.\-]+/)
        .map((w) => w.toLowerCase())
        .filter((w) => w.length > 0)
        .join(' ');
    return words.length > 0 ? words : 'a tool';
}
/**
 * Mid-sentence gerund phrase for a tool: curated override when one
 * exists, else `"using <humanized words>"`.
 */
export function toolGerund(name) {
    const override = TOOL_GERUND_OVERRIDES[name.trim()];
    if (override !== undefined)
        return override;
    return `using ${humanizeToolName(name)}`;
}
/**
 * Curated infinitive phrases per tool name — the approval-card framing
 * composes them after "wants to" ("<Agent> wants to run a command").
 * Mirrors `TOOL_GERUND_OVERRIDES`; keep both maps in sync.
 */
const TOOL_INFINITIVE_OVERRIDES = {
    ask_user_question: 'ask you a question',
    bash: 'run a command',
    code_execution: 'run code',
    str_replace_based_edit_tool: 'edit a file',
    text_editor: 'edit a file',
    web_search: 'search the web',
    web_fetch: 'read a web page',
};
/**
 * Infinitive phrase for a tool ("run a command" / "use matter
 * search"): curated override when one exists, else
 * `"use <humanized words>"`.
 */
export function toolInfinitive(name) {
    const override = TOOL_INFINITIVE_OVERRIDES[name.trim()];
    if (override !== undefined)
        return override;
    return `use ${humanizeToolName(name)}`;
}
/**
 * Standalone friendly activity one-liner for transcript rows:
 * `"Asking you a question…"` / `"Using matter search…"`.
 */
export function toolActivityLine(name) {
    const gerund = toolGerund(name);
    return `${gerund.charAt(0).toUpperCase()}${gerund.slice(1)}…`;
}
//# sourceMappingURL=humanize.js.map